import { useMemo } from 'react';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { BULK_EDIT_ASSETS } from '@elementary/common-ui/constants/queryKeys';
import { getBulkEditAssetsQuery } from './api';
import {
  BulkEditTableItem,
  type BulkEditAssetItem,
  type BulkEditAssetsResponse,
} from './types';

const useBulkEditAssets = (modelIds: string[]) => {
  const envId = useSelectedEnv();

  // We fetch by explicit ids, so a single page sized to the selection returns
  // every asset in one request — no pagination needed.
  const { data, isFetching } = useGraphqlQuery<BulkEditAssetItem[]>({
    query: getBulkEditAssetsQuery,
    queryKey: [BULK_EDIT_ASSETS, envId, modelIds],
    variables: {
      assetIds: modelIds.length ? modelIds : null,
      limit: modelIds.length,
    },
    select: (response: BulkEditAssetsResponse) => response.assetsPaged.items,
    enabled: modelIds.length > 0,
    envId,
  });

  const assets = useMemo<BulkEditTableItem[]>(
    () =>
      (data ?? []).map((asset) => ({
        unique_id: asset.id,
        name: asset.name,
        model_name: asset.name,
        source_name: asset.sourceName ?? undefined,
        tags: asset.tags ?? [],
        owners: asset.owners ?? [],
        description: asset.description ?? undefined,
      })),
    [data],
  );

  return { assets, isFetching };
};

export default useBulkEditAssets;
